/**
 * mpwa Ads SDK — vanilla TypeScript, single-bundle entry point.
 *
 * Usage:
 *
 *   <script src="https://cdn.mpwa.to/sdk.js" async></script>
 *   <div data-ad-unit="42" data-size="728x90"></div>
 *   <script>MpwaAds.init({ publisherId: 'PUB_ID' });</script>
 *
 * If `MpwaAds` is loaded before `init` is called, queued slots discovered via
 * `data-ad-unit` are picked up automatically on init.
 */
import { fetchAd, pixel } from './api.js';
import { renderAd } from './render.js';
import { deviceType, sessionId, uuid } from './runtime.js';
import type { AdRequestBody, AdResponse, SdkConfig } from './types.js';
import { collectEids } from './eids.js';
import { collectTopics } from './topics.js';
import { registerAttributionTrigger } from './ara.js';
import { joinFledgeInterestGroup, runProtectedAudience } from './fledge.js';
import { observeViewability } from './viewability.js';

const DEFAULT_AD_SERVER = 'https://ads.mpwa.to';

let config: Required<Pick<SdkConfig, 'adServerUrl' | 'autoRender' | 'debug'>> & {
  publisherId: string;
  userId?: string;
  sessionId?: string;
} = {
  adServerUrl: DEFAULT_AD_SERVER,
  autoRender: true,
  debug: false,
  publisherId: '',
};

let initialised = false;
const queue: HTMLElement[] = [];

/**
 * Initialise the SDK. Idempotent: subsequent calls override config but don't
 * re-render placed slots.
 */
export function init(cfg: SdkConfig): void {
  config = {
    adServerUrl: cfg.adServerUrl ?? DEFAULT_AD_SERVER,
    autoRender: cfg.autoRender ?? true,
    debug: cfg.debug ?? false,
    publisherId: String(cfg.publisherId),
    ...(cfg.userId ? { userId: cfg.userId } : {}),
    sessionId: cfg.sessionId ?? sessionId(),
  };
  if (initialised) {
    return;
  }
  initialised = true;

  // Drain any slots placed before `init` was called.
  scanDocument();
  if (queue.length > 0) {
    queue.splice(0).forEach((el) => void place(el));
  }

  // Pick up slots inserted later via MutationObserver.
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n instanceof HTMLElement) {
            findSlots(n).forEach((el) => void place(el));
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

/** Public API exposed on `window.MpwaAds`. */
export const place = async (slot: HTMLElement): Promise<AdResponse | undefined> => {
  if (!initialised) {
    queue.push(slot);
    return undefined;
  }
  const adUnitIdRaw = slot.getAttribute('data-ad-unit');
  const adUnitId = adUnitIdRaw === null ? NaN : Number(adUnitIdRaw);
  if (!Number.isFinite(adUnitId) || adUnitId <= 0) {
    log('skipping slot without valid data-ad-unit', slot);
    return undefined;
  }
  // Idempotency: refuse to re-place slots we've already processed in this page.
  if (slot.dataset.mpwaState === 'placed' || slot.dataset.mpwaState === 'rendered') {
    return undefined;
  }
  slot.dataset.mpwaState = 'placed';

  // Slot dimensions — used by the ad-server when the ad-unit is responsive
  // (width=0/height=0 in the catalog). For fixed-size units the server still
  // honours the stored size; sending these is cheap and forward-compatible.
  const slotWidth = Math.round(slot.clientWidth || slot.getBoundingClientRect().width);
  const slotHeight = Math.round(slot.clientHeight || slot.getBoundingClientRect().height);

  // Phase 8.2 — Privacy Sandbox Topics. Browser call is async with a hard
  // 60 ms ceiling inside collectTopics(); outside Chromium it resolves to
  // []. Either way the omitempty on the server keeps the wire cost at 0.
  const topics = await collectTopics();

  const body: AdRequestBody = {
    request_id: uuid(),
    ad_unit_id: adUnitId,
    page_url: location.href,
    ...(document.referrer ? { referrer: document.referrer } : {}),
    user: {
      ...(config.userId ? { id: config.userId } : {}),
      ...(config.sessionId ? { session: config.sessionId } : {}),
      // Best-effort universal-ID pickup. Empty arrays are omitted by the
      // ad-server JSON unmarshaller, so no extra bytes when the publisher
      // hasn't integrated any identity SDK.
      eids: collectEids(),
      topics,
    },
    device: { type: deviceType(), ua: navigator.userAgent },
    geo: {},
    slot: {
      ...(slotWidth ? { width: slotWidth } : {}),
      ...(slotHeight ? { height: slotHeight } : {}),
    },
  };

  let res: AdResponse;
  try {
    res = await fetchAd(config.adServerUrl, body);
  } catch (err) {
    slot.dataset.mpwaState = 'error';
    log('ad request failed', err);
    slot.dispatchEvent(new CustomEvent('mpwa:error', { detail: err, bubbles: true }));
    return undefined;
  }

  if (!res.served || !res.ad) {
    slot.dataset.mpwaState = 'no_fill';
    slot.dispatchEvent(new CustomEvent('mpwa:no_fill', { detail: res, bubbles: true }));
    return res;
  }

  // Phase 8.2 follow-up — opt the visitor into the FLEDGE interest group the
  // ad-server advised. Fire-and-forget; never blocks render. Future patch can
  // populate the bid hint from a CPM the ad-server adds to AdResponse.fledge;
  // until then the IG joins without one and just won't participate in the
  // next auction unless the publisher overrides via runProtectedAudience.
  if (res.fledge) {
    void joinFledgeInterestGroup(res.fledge);
  }

  if (config.autoRender) {
    // Responsive when the publisher's snippet said so (data-size="responsive"
    // or absent). Fixed-pixel sizing otherwise — a publisher who declared
    // data-size="300x250" expects exactly 300×250, not a stretched ad.
    const sizeAttr = (slot.getAttribute('data-size') ?? '').trim().toLowerCase();
    const isResponsive = sizeAttr === '' || sizeAttr === 'responsive' || sizeAttr === 'auto';
    const ad = res.ad;
    renderAd(slot, ad, { responsive: isResponsive });
    observeViewability(slot, () => {
      pixel(ad.beacons.impression);
    });
    slot.dataset.mpwaState = 'rendered';
    // AdSense-style auto-refresh: re-fetch when the slot has been on-screen
    // for the cycle length and the tab is still focused. Cancelled if the
    // publisher removes the slot from the DOM.
    scheduleRefresh(slot);
  } else {
    slot.dispatchEvent(new CustomEvent('mpwa:fill', { detail: res.ad, bubbles: true }));
  }

  return res;
};

const REFRESH_INTERVAL_MS = 30_000;
const refreshTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();
const refreshObservers = new WeakMap<HTMLElement, IntersectionObserver>();

/**
 * Tear down then re-arm the auto-refresh cycle for one slot.
 *
 * The cycle: 30s after a successful render we try to refresh. The slot must
 * be ≥50 % visible AND `document.visibilityState === 'visible'` (no tab in
 * the background). If either fails we re-arm and try again at the next tick.
 * Removing the slot from the DOM stops everything cleanly.
 */
function scheduleRefresh(slot: HTMLElement): void {
  // Clear any prior cycle for this slot.
  const prevTimer = refreshTimers.get(slot);
  if (prevTimer) clearTimeout(prevTimer);
  const prevObs = refreshObservers.get(slot);
  if (prevObs) prevObs.disconnect();

  // Track in-view state via IntersectionObserver — cheaper than polling.
  let inView = true;
  if (typeof IntersectionObserver !== 'undefined') {
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) inView = e.isIntersecting && e.intersectionRatio >= 0.5;
      },
      { threshold: [0, 0.5] },
    );
    obs.observe(slot);
    refreshObservers.set(slot, obs);
  }

  const tick = (): void => {
    if (!slot.isConnected) return; // slot removed by the publisher; stop.
    const tabHidden = typeof document !== 'undefined' && document.hidden;
    if (inView && !tabHidden) {
      slot.dataset.mpwaState = 'placed-refresh';
      void place(slot);
      // place() schedules its own next refresh on success, so we exit here.
      return;
    }
    // Otherwise try again later — gives a sluggish-but-correct experience
    // for an off-screen slot the user eventually scrolls back to.
    refreshTimers.set(slot, setTimeout(tick, REFRESH_INTERVAL_MS / 3));
  };
  refreshTimers.set(slot, setTimeout(tick, REFRESH_INTERVAL_MS));
}

function findSlots(root: ParentNode): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>('[data-ad-unit]');
  return Array.from(nodes);
}

function scanDocument(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanDocument, { once: true });
    return;
  }
  findSlots(document).forEach((el) => void place(el));
}

function log(...args: unknown[]): void {
  /* c8 ignore next 4 */
  if (config.debug) {
    // eslint-disable-next-line no-console
    console.log('[MpwaAds]', ...args);
  }
}

/**
 * Phase 8.2 — Attribution Reporting trigger for the merchant snippet.
 * Conversion pixel + `MpwaAds.registerConversion(url)` both end up calling
 * the same ARA primitive. Returns true when the browser accepted the
 * trigger registration, false on non-Chromium / disabled / missing url.
 */
export function registerConversion(triggerUrl: string): boolean {
  return registerAttributionTrigger(triggerUrl);
}

// Expose on global so the IIFE bundle is usable without an importer.
declare global {
  interface Window {
    MpwaAds?: {
      init: typeof init;
      place: typeof place;
      registerConversion: typeof registerConversion;
      runProtectedAudience: typeof runProtectedAudience;
      version: string;
    };
  }
}

if (typeof window !== 'undefined') {
  window.MpwaAds = {
    init,
    place,
    registerConversion,
    runProtectedAudience,
    version: '0.1.0',
  };
}
